import { useEffect, useState } from "react";

import type { DocumentSection, Validation } from "@/api/schemas";
import { cn } from "@/lib/utils";
import { perSectionStatus } from "./status";

interface SectionNavProps {
  sections: DocumentSection[];
  validation: Validation;
}

/**
 * Sticky in-tab navigator (W6 in the legacy template, now a real
 * component). Includes scroll-spy: the closest visible section gets a
 * highlighted indicator dot.
 */
export function SectionNav({ sections, validation }: SectionNavProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const sid = visible[0].target.id.replace(/^ds-/, "");
          setActiveId(sid);
        }
      },
      { rootMargin: "-80px 0px -50% 0px", threshold: [0, 0.25, 0.5, 1] },
    );
    for (const s of sections) {
      const el = document.getElementById(`ds-${s.section_id}`);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [sections]);

  if (!sections.length) return null;

  return (
    <nav className="sticky top-[88px] hidden h-fit w-[220px] flex-shrink-0 self-start rounded-md border border-stone-200 bg-white p-3 shadow-sm md:block">
      <h4 className="mb-2 text-[0.7rem] font-bold uppercase tracking-[0.05em] text-stone-400">
        U ovom dijelu
      </h4>
      <ul className="m-0 list-none space-y-0.5 p-0">
        {sections.map((s) => {
          const status = perSectionStatus(s.section_id, validation);
          const isActive = activeId === s.section_id;
          return (
            <li key={s.section_id}>
              <a
                href={`#ds-${s.section_id}`}
                className={cn(
                  "flex items-center gap-2 rounded px-2 py-1 text-[0.78rem] transition-colors",
                  "text-stone-500 hover:bg-stone-50 hover:text-stone-800",
                  isActive && "bg-ind-50 font-semibold text-ind-700",
                )}
                onClick={(event) => {
                  event.preventDefault();
                  scrollToSection(s.section_id);
                }}
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 flex-shrink-0 rounded-full",
                    status.kind === "ok" && "bg-green-500",
                    status.kind === "warn" && "bg-amber-500",
                    status.kind === "err" && "bg-red-500",
                  )}
                  aria-hidden
                />
                <span className="truncate">{s.title}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function scrollToSection(sectionId: string) {
  const el = document.getElementById(`ds-${sectionId}`);
  if (!el) return;
  el.dataset.open = "true"; // surfaced by DocSectionCard for keyboard navigation
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}
